/**
 * Plan all sections / Author all streams (David, Oct 28).
 *
 * Two steps with the coordinator's review between them: plan every section,
 * check the plans, then author every stream at once. The server queues the
 * work (two at a time) so PE and the AI service aren't flooded; each stream
 * shows its own progress here and on its tile. Finalize stays one stream at a
 * time, after review. Authoring leaves the live version up until the new one
 * is finalized.
 *
 * Wording is a first draft for David.
 */
import React, { useEffect, useState, useCallback } from "react";
import { sectionLabel } from "../../lib/sections.js";

const PROGRESS = {
  planning:       "Planning…",
  plan_ready:     "Plan ready — open the section to review it",
  plan_failed:    "Planning failed",
  generating:     "Authoring…",
  review_pending: "Ready for review",
  failed:         "Authoring failed",
};

/** What to show for a stream in each step: {enabled, checked, notes[]}. */
export function streamChoice(s, mode) {
  const notes = [];
  if (s.busy) return { enabled: false, checked: false, notes: ["Busy right now"] };
  if (mode === "plan") {
    if (s.kind !== "section") return null;
    if (s.plan_status === "plan_ready" && s.plan_scenes) notes.push("Has a plan — a new suggestion replaces it");
    if (!s.has_content) notes.push("Nothing in this section yet");
    return { enabled: true, checked: !!s.has_content && s.plan_status !== "plan_ready", notes };
  }
  // author
  if (s.kind === "section") {
    if (s.plan_status !== "plan_ready" || !s.plan_scenes) {
      return { enabled: false, checked: false, notes: ["No plan yet — plan it first"] };
    }
    if (!s.plan_reviewed) notes.push("Plan not reviewed yet");
  }
  if (s.draft_edited) notes.push("Your edits to its current draft will be replaced");
  else if (s.has_draft) notes.push("Its current draft will be replaced");
  if (s.live) notes.push("The live version stays up until you finalize the new one");
  return { enabled: true, checked: s.kind !== "section" || !!s.plan_reviewed, notes };
}

export default function StreamsBatchDialog({ clientId, mode, pronouns, onClose }) {
  const [streams, setStreams] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [started, setStarted] = useState(null);     // keys sent to the server
  const [skipped, setSkipped] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`/api/clients/${clientId}/streams`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setError(d.error || "Couldn't load the streams."); return null; }
    setStreams(d.streams || []);
    return d.streams || [];
  }, [clientId]);

  useEffect(() => {
    load().then(list => {
      if (!list) return;
      setPicked(new Set(list.filter(s => streamChoice(s, mode)?.checked).map(s => s.key)));
    });
  }, [load, mode]);

  // While work runs, refresh every few seconds.
  useEffect(() => {
    if (!started) return undefined;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [started, load]);

  const label = s => (s.kind === "section" ? sectionLabel(s.key, pronouns) : "Introduction stream");
  const rows = (streams || []).map(s => ({ s, c: streamChoice(s, mode) })).filter(x => x.c);

  async function start() {
    setBusy(true); setError(null);
    const keys = rows.filter(x => picked.has(x.s.key)).map(x => x.s.key);
    const url = mode === "plan" ? "plan-all" : "author-all";
    const body = mode === "plan" ? { sections: keys } : { streams: keys };
    try {
      const r = await fetch(`/api/clients/${clientId}/streams/${url}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't start.");
      setStarted(d.started || []);
      setSkipped(d.skipped || []);
      load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const title = mode === "plan" ? "Plan all sections" : "Author all streams";
  const running = started && streams && started.some(k => {
    const s = streams.find(x => x.key === k);
    return s && (s.status === "generating" || s.plan_status === "planning");
  });

  return (
    <div style={d.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={d.box} role="dialog" aria-label={title}>
        <div style={d.head}>
          <h2 style={d.title}>{title}</h2>
          <button style={d.close} onClick={onClose} aria-label="Close">×</button>
        </div>
        <p style={d.note}>
          {mode === "plan"
            ? "PE suggests a plan for each section you tick. Then open each section to check its plan before authoring."
            : "Each stream you tick is authored for your review. Finalize each one yourself after reviewing it."}
          {" "}They run two at a time; you can close this and come back.
        </p>
        {error && <p style={d.error} role="alert">{error}</p>}
        {!streams && !error && <p style={d.note}>Loading…</p>}
        {streams && (
          <ul style={d.list}>
            {rows.map(({ s, c }) => {
              const progress = started?.includes(s.key)
                ? PROGRESS[mode === "plan" ? s.plan_status : s.status] : null;
              return (
                <li key={s.key} style={d.row}>
                  <label style={{ ...d.label, ...(c.enabled ? {} : { opacity: 0.55 }) }}>
                    <input type="checkbox" disabled={!c.enabled || !!started} checked={picked.has(s.key)}
                      onChange={e => setPicked(p => { const n = new Set(p); e.target.checked ? n.add(s.key) : n.delete(s.key); return n; })} />
                    <span>{label(s)}</span>
                  </label>
                  {progress ? <span style={d.progress}>{progress}</span>
                    : c.notes.length > 0 && <span style={d.rowNote}>{c.notes.join(" · ")}</span>}
                </li>
              );
            })}
          </ul>
        )}
        {skipped.length > 0 && (
          <p style={d.note}>Not started: {skipped.map(x => label(rows.find(r => r.s.key === x.key)?.s || { key: x.key, kind: x.key === "intro" ? "intro" : "section" })).join(", ")} (busy, or no plan).</p>
        )}
        <div style={d.foot}>
          {!started ? (
            <>
              <button style={d.cancel} onClick={onClose}>Cancel</button>
              <button style={{ ...d.go, ...((busy || picked.size === 0) ? d.goOff : {}) }}
                disabled={busy || picked.size === 0} onClick={start}>
                {busy ? "Starting…" : `${mode === "plan" ? "Plan" : "Author"} ${picked.size} ${picked.size === 1 ? "stream" : "streams"}`}
              </button>
            </>
          ) : (
            <button style={d.go} onClick={onClose}>{running ? "Close — it keeps going" : "Done"}</button>
          )}
        </div>
      </div>
    </div>
  );
}

const d = {
  overlay:  { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  box:      { background: "var(--color-surface)", borderRadius: "var(--radius-xl)", padding: "var(--space-6)", width: 560, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "var(--space-3)" },
  head:     { display: "flex", alignItems: "center", justifyContent: "space-between" },
  title:    { margin: 0, fontSize: "var(--text-lg)" },
  close:    { background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--color-text-secondary)" },
  note:     { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  error:    { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-critical)" },
  list:     { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  row:      { display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: "var(--space-2)", borderBottom: "1px solid var(--color-border)", paddingBottom: "var(--space-2)" },
  label:    { display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--text-sm)", color: "var(--color-text-primary)" },
  rowNote:  { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  progress: { fontSize: "var(--text-xs)", color: "var(--color-accent)", fontWeight: "var(--weight-medium)" },
  foot:     { display: "flex", justifyContent: "flex-end", gap: "var(--space-3)", paddingTop: "var(--space-2)" },
  cancel:   { font: "inherit", fontSize: "var(--text-sm)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer", color: "var(--color-text-primary)" },
  go:       { font: "inherit", fontSize: "var(--text-sm)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  goOff:    { opacity: 0.5, cursor: "not-allowed" },
};
